import { useCallback, useEffect, useState } from 'react';
import { FileSpreadsheet, Download, AlertTriangle, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { tax } from '../lib/api';
import { fmtStamp } from './LiveClock';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

function MonthPicker({ month, setMonth, onCsv, testid }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input type="month" data-testid={`${testid}-month`} value={month} onChange={(e) => setMonth(e.target.value)}
        className="border-2 border-ink/15 rounded-lg px-3 h-10 text-sm outline-none focus:border-ink"/>
      <button data-testid={`${testid}-csv`} onClick={onCsv} className="btn-outline !py-2 !px-3 text-xs"><Download className="w-3.5 h-3.5"/>CSV</button>
    </div>
  );
}

const download = async (fn, name) => {
  try {
    const blob = await fn();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch { toast.error('Download failed'); }
};

function Stat({ label, value, testid, tone = 'ink' }) {
  const tones = { ink: 'text-ink', red: 'text-[#B71C1C]', green: 'text-[#1B5E20]' };
  return (
    <div className="rounded-xl bg-white border-2 border-ink/10 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/60">{label}</div>
      <div data-testid={testid} className={`font-heading font-black text-xl tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export function SellerTaxStatement() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const load = useCallback(() => tax.mine(month).then(setData).catch(() => toast.error('Could not load statement')), [month]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-ink/60">Loading…</div>;
  const t = data.totals;
  return (
    <div data-testid="seller-tax-statement" className="card-soft p-5">
      <div className="flex items-center gap-2 flex-wrap">
        <Receipt className="w-5 h-5 text-[#0D47A1]"/>
        <div className="font-heading font-bold text-lg">Tax statement (TDS & TCS)</div>
        <div className="ml-auto"><MonthPicker month={month} setMonth={setMonth} testid="seller-tax" onCsv={() => download(() => tax.mineCsv(month), `tax-statement-${month}.csv`)}/></div>
      </div>
      <div className="text-xs text-ink/60 mt-1">Deducted by {data.company.legal_name} · TAN {data.company.tan} · GSTIN {data.company.gstin}</div>
      {!data.seller_pan_on_file && (
        <div data-testid="seller-tax-no-pan" className="mt-3 rounded-lg bg-[#FFF3E0] text-[#9A4500] text-xs font-bold p-3 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0"/>Your PAN is not on file, so TDS is cut at {data.company.tds_rate_no_pan_pct}% instead of {data.company.tds_rate_pct}%. Add PAN in “KYC & Bank”.
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Orders" value={t.orders} testid="seller-tax-orders"/>
        <Stat label="Gross sales" value={inr(t.gross)} testid="seller-tax-gross"/>
        <Stat label="TDS cut" value={inr(t.tds_amount)} testid="seller-tax-tds" tone="red"/>
        <Stat label="TCS cut" value={inr(t.tcs_amount)} testid="seller-tax-tcs" tone="red"/>
        <Stat label="Net payout" value={inr(t.payout)} testid="seller-tax-payout" tone="green"/>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-ink/60 border-b border-ink/10">
            <th className="py-2 pr-2">Txn serial</th><th className="pr-2">Delivered</th><th className="pr-2 text-right">Gross</th>
            <th className="pr-2 text-right">TDS</th><th className="pr-2 text-right">TCS</th><th className="text-right">Payout</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.order_id} data-testid={`seller-tax-row-${r.order_id}`} className="border-b border-ink/5">
                <td className="py-2 pr-2 font-mono text-[10px]">{r.txn_serial}</td>
                <td className="pr-2 whitespace-nowrap">{fmtStamp(r.delivered_at)}</td>
                <td className="pr-2 text-right tabular-nums">{inr(r.gross)}</td>
                <td className="pr-2 text-right tabular-nums">{inr(r.tds_amount)} <span className="text-ink/40">@{r.tds_rate_pct}%</span></td>
                <td className="pr-2 text-right tabular-nums">{inr(r.tcs_amount)} <span className="text-ink/40">@{r.tcs_rate_pct}%</span></td>
                <td className="text-right tabular-nums font-bold">{inr(r.payout)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.rows.length && <div data-testid="seller-tax-empty" className="text-center text-sm text-ink/60 py-6">No delivered orders in {month}.</div>}
      </div>
      <p className="text-[11px] text-ink/60 mt-3">{data.note}</p>
    </div>
  );
}

export function AdminTaxStatements() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const load = useCallback(() => tax.admin(month).then(setData).catch(() => toast.error('Could not load register')), [month]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="mt-6 text-sm text-ink/60">Loading…</div>;
  return (
    <div data-testid="admin-tax-statements" className="mt-6 card-soft p-5">
      <div className="flex items-center gap-2 flex-wrap">
        <FileSpreadsheet className="w-5 h-5 text-[#0D47A1]"/>
        <div className="font-heading font-bold text-lg">TDS / TCS register — all sellers</div>
        <div className="ml-auto"><MonthPicker month={month} setMonth={setMonth} testid="admin-tax" onCsv={() => download(() => tax.adminCsv(month), `tds-tcs-register-${month}.csv`)}/></div>
      </div>
      <div className="text-xs text-ink/60 mt-1">Deductor {data.company.legal_name} · PAN {data.company.pan} · TAN {data.company.tan} · TDS {data.company.tds_rate_pct}% · TCS {data.company.tcs_rate_pct}%</div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Sellers" value={data.totals.sellers} testid="admin-tax-sellers"/>
        <Stat label="Gross sales" value={inr(data.totals.gross)} testid="admin-tax-gross"/>
        <Stat label="TDS to deposit" value={inr(data.totals.tds_amount)} testid="admin-tax-tds" tone="red"/>
        <Stat label="TCS to file (GSTR-8)" value={inr(data.totals.tcs_amount)} testid="admin-tax-tcs" tone="red"/>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-ink/60 border-b border-ink/10">
            <th className="py-2 pr-2">Seller</th><th className="pr-2">PAN</th><th className="pr-2 text-right">Orders</th><th className="pr-2 text-right">Gross</th>
            <th className="pr-2 text-right">TDS</th><th className="pr-2 text-right">TCS</th><th className="pr-2">Txn serials</th>
          </tr></thead>
          <tbody>
            {data.sellers.map((s) => (
              <tr key={s.owner_id} data-testid={`admin-tax-row-${s.owner_id}`} className="border-b border-ink/5">
                <td className="py-2 pr-2"><div className="font-bold">{s.seller_name || '—'}</div><div className="text-ink/50">{s.stores.join(', ')}</div></td>
                <td className="pr-2">{s.pan_on_file ? 'On file' : <span className="text-[#B71C1C] font-bold">Missing</span>}</td>
                <td className="pr-2 text-right">{s.orders}</td>
                <td className="pr-2 text-right tabular-nums">{inr(s.gross)}</td>
                <td className="pr-2 text-right tabular-nums">{inr(s.tds_amount)}</td>
                <td className="pr-2 text-right tabular-nums">{inr(s.tcs_amount)}</td>
                <td className="pr-2 font-mono text-[9px] text-ink/60">{s.serial_range[0]}<br/>{s.serial_range[1]}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.sellers.length && <div data-testid="admin-tax-empty" className="text-center text-sm text-ink/60 py-6">No delivered orders in {month}.</div>}
      </div>
    </div>
  );
}
